import type { EstadoCanvas } from '../dominio/assistente.interface'

type Formulario = Extract<EstadoCanvas, { type: 'form' }>

export const CHAVE_DOS_RASCUNHOS_DA_ACAO = 'assistentePontotel:rascunhosAcao:v1'

export interface IdentidadeDoRascunho {
  pessoaId: string
  empresaId: string
  sessaoId: string
  interacaoId: string
}

interface RascunhoDaAcao extends IdentidadeDoRascunho {
  estruturaId: string
  valores: Record<string, string>
}

interface RegistroDeRascunhos {
  versao: 1
  rascunhos: RascunhoDaAcao[]
}

export function estruturaDoFormulario (formulario: Formulario): string {
  return JSON.stringify({ capability: formulario.capability, schema: formulario.schema })
}

function armazenamento (): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage } catch { return null }
}

function lerRegistro (): RegistroDeRascunhos {
  try {
    const texto = armazenamento()?.getItem(CHAVE_DOS_RASCUNHOS_DA_ACAO)
    if (!texto) return { versao: 1, rascunhos: [] }
    const lido = JSON.parse(texto) as Partial<RegistroDeRascunhos>
    if (lido.versao !== 1 || !Array.isArray(lido.rascunhos)) {
      limparTodosOsRascunhos()
      return { versao: 1, rascunhos: [] }
    }
    return { versao: 1, rascunhos: lido.rascunhos.filter(ehRascunhoValido) }
  } catch {
    limparTodosOsRascunhos()
    return { versao: 1, rascunhos: [] }
  }
}

function ehRascunhoValido (valor: unknown): valor is RascunhoDaAcao {
  if (!valor || typeof valor !== 'object') return false
  const registro = valor as Record<string, unknown>
  return ['pessoaId', 'empresaId', 'sessaoId', 'interacaoId', 'estruturaId']
    .every(chave => typeof registro[chave] === 'string' && Boolean(registro[chave])) &&
    Boolean(registro.valores) && typeof registro.valores === 'object' &&
    !Array.isArray(registro.valores) &&
    Object.values(registro.valores as Record<string, unknown>).every(campo => typeof campo === 'string')
}

function escreverRegistro (registro: RegistroDeRascunhos): void {
  try {
    const destino = armazenamento()
    if (!destino) return
    if (registro.rascunhos.length === 0) destino.removeItem(CHAVE_DOS_RASCUNHOS_DA_ACAO)
    else destino.setItem(CHAVE_DOS_RASCUNHOS_DA_ACAO, JSON.stringify(registro))
  } catch { /* A digitação na memória segue utilizável quando o storage falha. */ }
}

function mesmosDados (a: IdentidadeDoRascunho, b: IdentidadeDoRascunho): boolean {
  return a.pessoaId === b.pessoaId && a.empresaId === b.empresaId &&
    a.sessaoId === b.sessaoId && a.interacaoId === b.interacaoId
}

function apenasCamposDoFormulario (formulario: Formulario, valores: Record<string, string>): Record<string, string> {
  return Object.fromEntries(formulario.schema.map(campo => [campo.name,
    typeof valores[campo.name] === 'string' ? valores[campo.name] : (formulario.values[campo.name] || '')
  ]))
}

export function guardarRascunho (
  identidade: IdentidadeDoRascunho,
  formulario: Formulario,
  valores: Record<string, string>
): void {
  const atual = lerRegistro()
  const rascunho: RascunhoDaAcao = {
    ...identidade,
    estruturaId: estruturaDoFormulario(formulario),
    valores: apenasCamposDoFormulario(formulario, valores)
  }
  escreverRegistro({ versao: 1, rascunhos: [
    ...atual.rascunhos.filter(item => !mesmosDados(item, identidade)), rascunho
  ] })
}

export function recuperarRascunho (
  identidade: IdentidadeDoRascunho,
  formulario: Formulario
): Record<string, string> | null {
  const atual = lerRegistro()
  const esperado = estruturaDoFormulario(formulario)
  const registro = atual.rascunhos.find(item => item.interacaoId === identidade.interacaoId)
  if (!registro) return null
  if (!mesmosDados(registro, identidade) || registro.estruturaId !== esperado) {
    escreverRegistro({ versao: 1, rascunhos: atual.rascunhos.filter(item => item !== registro) })
    return null
  }
  return apenasCamposDoFormulario(formulario, registro.valores)
}

export function limparRascunhosDaConversa (identidade: Pick<IdentidadeDoRascunho, 'pessoaId' | 'empresaId' | 'sessaoId'>): void {
  const atual = lerRegistro()
  escreverRegistro({ versao: 1, rascunhos: atual.rascunhos.filter(item =>
    item.pessoaId !== identidade.pessoaId || item.empresaId !== identidade.empresaId || item.sessaoId !== identidade.sessaoId
  ) })
}

export function limparRascunhosDeOutraIdentidade (identidade: Pick<IdentidadeDoRascunho, 'pessoaId' | 'empresaId' | 'sessaoId'>): void {
  const atual = lerRegistro()
  escreverRegistro({ versao: 1, rascunhos: atual.rascunhos.filter(item =>
    item.pessoaId === identidade.pessoaId && item.empresaId === identidade.empresaId && item.sessaoId === identidade.sessaoId
  ) })
}

export function limparTodosOsRascunhos (): void {
  try { armazenamento()?.removeItem(CHAVE_DOS_RASCUNHOS_DA_ACAO) } catch { /* storage bloqueado */ }
}
